import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import * as api from '../services/api';
import { jwtDecode } from 'jwt-decode';

// 1. Crear el contexto
export const AuthContext = createContext();

// Hook personalizado para usar el contexto fÃƒÂ¡cilmente
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Proveedor del contexto con manejo de loading y mÃƒÂºltiples mÃƒÂ©todos de auth
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requireRut, setRequireRut] = useState(false);
  const [rutValue, setRutValue] = useState('');
  const [requireTelefono, setRequireTelefono] = useState(false);
  const [telefonoValue, setTelefonoValue] = useState('');
  const [rutSaving, setRutSaving] = useState(false);
  const [rutError, setRutError] = useState('');

  // RUT helpers: clean, format, DV validation (mod 11)
  const cleanRut = (input) => (input || '').toString().replace(/\.|-/g, '').replace(/\s+/g, '').toUpperCase();
  const formatRut = (input) => {
    const v = cleanRut(input);
    if (v.length === 0) return '';
    const body = v.slice(0, -1);
    const dv = v.slice(-1);
    // Add thousands separators to body
    const reversed = body.split('').reverse();
    const chunks = [];
    for (let i = 0; i < reversed.length; i += 3) {
      chunks.push(reversed.slice(i, i + 3).join(''));
    }
    const withDots = chunks.map((c) => c.split('').reverse().join('')).reverse().join('.');
    return `${withDots}${withDots ? '-' : ''}${dv}`;
  };
  const computeDV = (numStr) => {
    let sum = 0;
    let mul = 2;
    for (let i = numStr.length - 1; i >= 0; i--) {
      sum += parseInt(numStr[i], 10) * mul;
      mul = mul === 7 ? 2 : mul + 1;
    }
    const res = 11 - (sum % 11);
    if (res === 11) return '0';
    if (res === 10) return 'K';
    return String(res);
  };
  const normalizeRut = (input) => {
    const cleaned = cleanRut(input);
    if (cleaned.length < 2) return cleaned.toUpperCase();
    const body = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1).toUpperCase();
    return `${body}-${dv}`;
  };
  const isRutValid = (rut) => {
    const norm = normalizeRut(rut);
    const [body, dv] = norm.split('-');
    if (!body || !dv || !/^\d+$/.test(body) || !/^[\dK]$/.test(dv)) return false;
    return computeDV(body) === dv;
  };

  // Normalize user name fields so nombre = first name, apellido = first surname
  const normalizeUserNameFields = (u) => {
    try {
      if (!u) return u;
      const nombreRaw = (u?.nombre || u?.name || '').trim();
      const apellidoRaw = (u?.apellido || '').trim();
      if (!nombreRaw && !apellidoRaw) return u;

      const nombreParts = (nombreRaw || '').split(/\s+/).filter(Boolean);
      const apellidoParts = (apellidoRaw || '').split(/\s+/).filter(Boolean);

      const firstName = nombreParts[0] || '';
      let firstSurname = '';

      if (apellidoParts.length > 0) {
        firstSurname = apellidoParts[0];
      }
      if (nombreParts.length >= 4) {
        firstSurname = nombreParts[nombreParts.length - 2];
      } else if (nombreParts.length === 3) {
        firstSurname = nombreParts[1];
      } else if (nombreParts.length === 2 && !firstSurname) {
        firstSurname = nombreParts[1];
      }
      // Handle cases where apellido likely contains only the second surname
      if (apellidoParts.length === 1 && nombreParts.length >= 3) {
        firstSurname = nombreParts[nombreParts.length - 1];
      }

      const properCase = (word) => {
        if (!word) return '';
        return word
          .toLowerCase()
          .split('-')
          .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ''))
          .join('-');
      };

      return {
        ...u,
        nombre: properCase(firstName) || u?.nombre || '',
        apellido: properCase(firstSurname) || u?.apellido || '',
      };
    } catch (_) {
      return u;
    }
  };

  const syncUserProfile = React.useCallback(async () => {
    try {
      const { data: meData } = await api.getMe();
      let enrichedData = { ...meData };

      // If collaborator and no image, try to fetch from public/team list
      if (enrichedData.role === 'colaborador' && !enrichedData.imagen_url) {
        try {
          const { data: colabs } = await api.getColaboradores({ activo: 1 });
          const list = Array.isArray(colabs) ? colabs : (colabs?.data || []);

          const norm = (s) => (s || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
          const meId = String(enrichedData.id || '');
          const meEmail = norm(enrichedData.email);
          const meFull = norm(`${enrichedData.nombre} ${enrichedData.apellido}`).replace(/\s+/g, ' ');

          const found = list.find(c => {
            const cId = String(c.usuario_id || '');
            if (cId && meId && cId === meId) return true;

            const cEmail = norm(c.email);
            if (cEmail && meEmail && cEmail === meEmail) return true;

            // Relaxed Name Match: Check if one full name contains the other
            const cFull = norm(`${c.nombre} ${c.apellido}`).replace(/\s+/g, ' ');
            if (meFull && cFull) {
              // Check exact containment or token overlap
              if (cFull.includes(meFull) || meFull.includes(cFull)) return true;
            }
            return false;
          });

          if (found) {
            const img = found.imagen_url || found.imagen || found.foto || found.avatar;
            if (img) enrichedData.imagen_url = img;
          }
        } catch (err) {
          console.warn('Could not fetch collaborator details for image', err);
        }
      }

      // Update user state with potentially new data (image, role, etc)
      setUser((prev) => normalizeUserNameFields({ ...(prev || {}), ...enrichedData }));

      // Check RUT and Telefono requirement
      const hasRut = !!(enrichedData && enrichedData.rut);
      const hasTelefono = !!(enrichedData && enrichedData.telefono);
      setRequireRut(!hasRut);
      setRequireTelefono(!hasTelefono);
      if (!hasRut) setRutValue('');
      if (!hasTelefono) setTelefonoValue('');
    } catch (_) {
      // If token invalid, interceptor will redirect elsewhere if needed.
      setRequireRut(false);
      setRequireTelefono(false);
    }
  }, []);

  useEffect(() => {
    // Restaurar sesión en carga inicial y verificar datos
    (async () => {
      try {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          const decodedUser = jwtDecode(storedToken);
          setUser(normalizeUserNameFields(decodedUser.data));
          // Fetch fresh data + image
          await syncUserProfile();
        }
      } catch (error) {
        console.error('Failed to decode token on initial load', error);
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    })();
  }, [syncUserProfile]);

  const login = async (credentials) => {
    try {
      const response = await api.login(credentials);
      if (response.success && response.token) {
        const newToken = response.token;
        const decodedUser = jwtDecode(newToken);
        localStorage.setItem('token', newToken);
        setUser(normalizeUserNameFields(decodedUser.data));
        await syncUserProfile();
        return { success: true, user: decodedUser.data };
      } else {
        return { success: false, error: response.error || 'Error al iniciar sesiÃƒÂ³n' };
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Credenciales invÃƒÂ¡lidas o error del servidor.';
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const response = await api.loginWithGoogle(idToken);
      if (response.success && response.token) {
        const newToken = response.token;
        const decodedUser = jwtDecode(newToken);
        localStorage.setItem('token', newToken);
        setUser(normalizeUserNameFields(decodedUser.data));
        await syncUserProfile();
        return { success: true, user: decodedUser.data };
      } else {
        return { success: false, error: response.error || 'Error con Google Sign-In' };
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error con Google Sign-In.';
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.register(userData);
      // Si el backend devuelve token directamente tras registrar
      if (response?.data?.token) {
        const newToken = response.data.token;
        const decodedUser = jwtDecode(newToken);
        localStorage.setItem('token', newToken);
        setUser(decodedUser.data);
        return { success: true, user: decodedUser.data, message: response.data.message };
      }
      // Si no devuelve token, intentar login con las mismas credenciales
      if (userData?.email && userData?.password) {
        const loginRes = await api.login({ email: userData.email, password: userData.password });
        if (loginRes.success && loginRes.token) {
          const decodedUser = jwtDecode(loginRes.token);
          localStorage.setItem('token', loginRes.token);
          setUser(decodedUser.data);
          return { success: true, user: decodedUser.data, message: response?.data?.message };
        }
      }
      // Fallback: registrado, pero sin token
      return { success: true, message: response?.data?.message || 'Registro exitoso' };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error al registrar el usuario.';
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('token');
    api.logout();
    setRequireRut(false);
    setRequireTelefono(false);
    setRutValue('');
    setTelefonoValue('');
    setRutError('');
  };

  // Refresh user profile from backend and merge into user state
  const refreshMe = async () => {
    try {
      const { data } = await api.getMe();
      let enriched = { ...data };

      if (enriched.role === 'colaborador' && !enriched.imagen_url) {
        try {
          const { data: colabs } = await api.getColaboradores({ activo: 1 });
          const list = Array.isArray(colabs) ? colabs : (colabs?.data || []);

          const norm = (s) => (s || '').toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
          const meId = String(enriched.id || '');
          const meEmail = norm(enriched.email);
          const meFull = norm(`${enriched.nombre} ${enriched.apellido}`).replace(/\s+/g, ' ');

          const found = list.find(c => {
            const cId = String(c.usuario_id || '');
            if (cId && meId && cId === meId) return true;

            const cEmail = norm(c.email);
            if (cEmail && meEmail && cEmail === meEmail) return true;

            const cFull = norm(`${c.nombre} ${c.apellido}`).replace(/\s+/g, ' ');
            if (meFull && cFull) {
              if (cFull.includes(meFull) || meFull.includes(cFull)) return true;
            }
            return false;
          });

          if (found) {
            const img = found.imagen_url || found.imagen || found.foto || found.avatar;
            if (img) enriched.imagen_url = img;
          }
        } catch (_) { }
      }

      setUser((prev) => normalizeUserNameFields({ ...(prev || {}), ...enriched }));
      return enriched;
    } catch (_) {
      return null;
    }
  };

  // Compute formatted display name and initials (First Name + First Surname, Proper Case)
  const nameComputed = useMemo(() => {
    const properCase = (word) => {
      if (!word) return '';
      return word
        .toLowerCase()
        .split('-')
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ''))
        .join('-');
    };

    if (!user) {
      return { displayName: 'Usuario', initials: 'U' };
    }

    const nombreRaw = (user?.nombre || user?.name || '').trim();
    const apellidoRaw = (user?.apellido || '').trim();

    const nombreParts = nombreRaw.split(/\s+/).filter(Boolean);
    const apellidoParts = apellidoRaw.split(/\s+/).filter(Boolean);

    const firstName = nombreParts[0] || '';
    let firstSurname = '';

    if (apellidoParts.length > 0) {
      firstSurname = apellidoParts[0];
    } else if (nombreParts.length >= 4) {
      // 4+ words: assume penultimate is first surname (e.g., "Francisca Jesus Leiva Bruce" -> "Leiva")
      firstSurname = nombreParts[nombreParts.length - 2];
    } else if (nombreParts.length === 3) {
      // 3 words: assume second is first surname (e.g., "Francisca Leiva Bruce" -> "Leiva")
      firstSurname = nombreParts[1];
    } else if (nombreParts.length === 2) {
      // 2 words: assume second is surname (e.g., "Ana Ruiz" -> "Ruiz")
      firstSurname = nombreParts[1];
    }

    // Override heuristic: if nombre has 3+ parts and apellido has exactly 1 part,
    // it's common that apellido holds the second surname and the first surname is at the end of nombre.
    // Example: nombre="FRANCISCA JESUS LEIVA", apellido="BRUCE" -> use "LEIVA" as first surname.
    if (apellidoParts.length === 1 && nombreParts.length === 3) {
      const lastFromNombre = nombreParts[nombreParts.length - 1];
      if (lastFromNombre && lastFromNombre.toLowerCase() !== (firstSurname || '').toLowerCase()) {
        firstSurname = lastFromNombre;
      }
    }

    const firstNamePC = properCase(firstName);
    const firstSurnamePC = properCase(firstSurname);
    const displayName = `${firstNamePC} ${firstSurnamePC}`.trim() || 'Usuario';
    const initials = `${(firstNamePC[0] || 'U').toUpperCase()}${(firstSurnamePC[0] || '').toUpperCase()}`.trim();

    return { displayName, initials };
  }, [user]);

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshMe,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isColaborador: user?.role === 'colaborador',
    isMiembro: user?.role === 'miembro',
    isPaciente: user?.role === 'paciente',
    displayName: nameComputed.displayName,
    initials: nameComputed.initials,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      {(requireRut || requireTelefono) && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60" />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-xl border border-slate-200 shadow-lg p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Completa tu Perfil</h3>
            <p className="text-sm text-slate-600 mb-4">Para continuar, necesitamos los siguientes datos para verificar tu identidad de forma segura.</p>
            {rutError && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{rutError}</div>
            )}
            <div className="space-y-4">
              {requireRut && (
                <div>
                  <label className="block text-sm text-slate-700 mb-1">RUT *</label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    placeholder="12.345.678-9"
                    value={rutValue}
                    onChange={(e) => {
                      setRutError('');
                      setRutValue(formatRut(e.target.value));
                    }}
                  />
                </div>
              )}
              {requireTelefono && (
                <div>
                  <label className="block text-sm text-slate-700 mb-1">Teléfono *</label>
                  <input
                    type="tel"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    placeholder="Ej: +56912345678"
                    value={telefonoValue}
                    onChange={(e) => {
                      setRutError('');
                      setTelefonoValue(e.target.value);
                    }}
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors text-white shadow-xs h-9 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  onClick={async () => {
                    try {
                      setRutError('');
                      const payload = {};

                      if (requireRut) {
                        const val = rutValue.trim();
                        if (!isRutValid(val)) {
                          setRutError('RUT inválido');
                          return;
                        }
                        payload.rut = normalizeRut(val);
                      }

                      if (requireTelefono) {
                        const val = telefonoValue.trim();
                        if (!val) {
                          setRutError('El teléfono es obligatorio');
                          return;
                        }
                        if (!/^[0-9+\s-]*$/.test(val)) {
                          setRutError('El teléfono solo puede contener números, espacios, + o -.');
                          return;
                        }
                        const digitsOnly = val.replace(/[^0-9]/g, '');
                        if (digitsOnly.length < 8 || digitsOnly.length > 20) {
                          setRutError('Ingresa un teléfono válido (entre 8 y 20 dígitos).');
                          return;
                        }
                        payload.telefono = val.replace(/\s+/g, '');
                      }

                      setRutSaving(true);
                      await api.completeProfile(payload);
                      await syncUserProfile();
                    } catch (e) {
                      if (e?.response?.data?.message === 'RUT ya registrado') {
                        setRutError('Este RUT ya está registrado. Si crees que es un error, por favor contáctanos a soporte@integramentehwh.com');
                      } else {
                        setRutError(e?.response?.data?.message || 'No se pudieron guardar los datos. Si el problema persiste, contáctanos a soporte@integramentehwh.com');
                      }
                    } finally {
                      setRutSaving(false);
                    }
                  }}
                  disabled={rutSaving}
                >
                  {rutSaving ? 'Guardando…' : 'Guardar y continuar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};



